import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBatch } from '@/contexts/BatchContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/components/ui/use-toast';
import { InlineSpinner } from '@/components/ui/loading-spinner';
import { ArrowLeft, Upload, Download, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { studentApi } from '@/lib/api';
import { Student } from '@/types';

const ImportStudentsPage = () => {
  const navigate = useNavigate();
  const { currentBatch } = useBatch();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importResults, setImportResults] = useState<{
    success: boolean;
    importedStudents: Student[];
    errors: string[];
  } | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      const isValidFile = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

      if (!isValidFile) {
        toast({
          title: 'Invalid File Type',
          description: 'Please select an Excel file (.xlsx or .xls).',
          variant: 'destructive',
        });
        return;
      }

      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast({
          title: 'File Too Large',
          description: 'Please select a file smaller than 5MB.',
          variant: 'destructive',
        });
        return;
      }

      setSelectedFile(file);
      setImportResults(null);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      const isValidFile = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

      if (!isValidFile) {
        toast({
          title: 'Invalid File Type',
          description: 'Please select an Excel file (.xlsx or .xls).',
          variant: 'destructive',
        });
        return;
      }

      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast({
          title: 'File Too Large',
          description: 'Please select a file smaller than 5MB.',
          variant: 'destructive',
        });
        return;
      }

      setSelectedFile(file);
      setImportResults(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast({
        title: 'Missing Information',
        description: 'Please select a file to import.',
        variant: 'destructive',
      });
      return;
    }

    if (!currentBatch) {
      toast({
        title: 'Missing Batch Information',
        description: 'Please ensure a batch is selected.',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const importedStudents = await studentApi.import(selectedFile, currentBatch.id);

      clearInterval(progressInterval);
      setUploadProgress(100);

      setImportResults({
        success: true,
        importedStudents,
        errors: []
      });

      toast({
        title: 'Import Successful',
        description: `Successfully imported ${importedStudents.length} students.`,
      });

    } catch (error) {
      console.error('Import error:', error);
      setImportResults({
        success: false,
        importedStudents: [],
        errors: ['Failed to import students. Please check your file format and try again.']
      });

      toast({
        title: 'Import Failed',
        description: 'Failed to import students. Please check your file and try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const downloadTemplate = () => {
    const csvContent = 'firstName,lastName,email,studentId,batchId,classIds\nJohn,Doe,john.doe@example.com,STU001,,CLASS001;CLASS002\nJane,Smith,jane.smith@example.com,STU002,,CLASS001';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link to="/students">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-3xl font-bold tracking-tight">Import Students</h1>
      </div>

      {/* Instructions Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Import Instructions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-medium">CSV File Format</h4>
            <p className="text-sm text-muted-foreground">
              Your CSV file should contain the following columns in order:
            </p>
            <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
              <li><strong>firstName</strong> - Student's first name (required)</li>
              <li><strong>lastName</strong> - Student's last name (required)</li>
              <li><strong>email</strong> - Student's email address (required)</li>
              <li><strong>studentId</strong> - Unique student ID (optional, will be auto-generated if not provided)</li>
              <li><strong>batchId</strong> - Batch ID (optional, will use current batch if not provided)</li>
              <li><strong>classIds</strong> - Class IDs separated by semicolons or commas (optional, e.g., "CLASS001;CLASS002")</li>
            </ul>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Students will be imported into the current batch ({currentBatch?.year || 'No batch selected'})
                unless a specific batchId is provided in the CSV file.
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadTemplate}>
              <Download className="mr-2 h-4 w-4" />
              Download Template
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* File Upload Card */}
      <Card>
        <CardHeader>
          <CardTitle>Upload Student File</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center hover:border-primary/50 transition-colors duration-200"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <div className="space-y-2">
              <p className="text-sm font-medium">
                Drop your Excel file here or click to browse
              </p>
              <p className="text-xs text-muted-foreground">
                Supports Excel files (.xlsx, .xls) up to 5MB
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="mt-4"
            >
              Select File
            </Button>
          </div>

          {selectedFile && (
            <Alert>
              <FileText className="h-4 w-4" />
              <AlertDescription>
                Selected file: <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024).toFixed(1)} KB)
              </AlertDescription>
            </Alert>
          )}

          {isUploading && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <InlineSpinner size="sm" />
                <span className="text-sm">Importing students...</span>
              </div>
              <Progress value={uploadProgress} className="w-full" />
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={handleImport}
              disabled={!selectedFile || isUploading}
              className="flex-1"
            >
              {isUploading ? (
                <>
                  <InlineSpinner size="sm" className="mr-2" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Import Students
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results Card */}
      {importResults && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {importResults.success ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600" />
              )}
              Import Results
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {importResults.success ? (
              <div className="space-y-2">
                <p className="text-green-600 font-medium">
                  Successfully imported {importResults.importedStudents.length} students
                </p>
                <div className="flex gap-2">
                  <Button onClick={() => navigate('/students')}>
                    View Students
                  </Button>
                  <Button variant="outline" onClick={() => {
                    setSelectedFile(null);
                    setImportResults(null);
                  }}>
                    Import More
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-red-600 font-medium">Import failed</p>
                {importResults.errors.map((error, index) => (
                  <p key={index} className="text-sm text-red-600">• {error}</p>
                ))}
                <Button variant="outline" onClick={() => setImportResults(null)}>
                  Try Again
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ImportStudentsPage;
